// src/views/AdminUsuarios.jsx
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { format, parseISO, differenceInDays, isBefore, addDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AdminMenu from '../components/AdminMenu';

function AdminUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editando, setEditando] = useState(null);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [form, setForm] = useState({
    username: '',
    password: '',
    servidor: 'Mundiemby1',
    fechaExp: format(new Date(), 'yyyy-MM-dd'),
    maxDispositivos: 1,
    isAdmin: false,
    telefono: '',
    telegram: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaExacta, setBusquedaExacta] = useState(false);
  const [filtroServidor, setFiltroServidor] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [resultadosPorPagina, setResultadosPorPagina] = useState(10);
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);
  const [orden, setOrden] = useState({ campo: 'username', direccion: 'asc' });
  const navigate = useNavigate();

  const servidores = ['Mundiemby1','Mundiemby2','Mundiemby3','Mundiemby4','Mundiemby5','Mundiemby6','Mundiemby7'];

  useEffect(() => { cargarUsuarios(); }, []);

  const cargarUsuarios = async () => {
    setLoading(true);
    try {
      const res = await api.get('/usuarios');
      setUsuarios(res.data);
    } catch {
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const iniciarEdicion = (usuario) => {
    setEditando(usuario);
    setForm({
      username: usuario.username,
      password: '',
      servidor: usuario.servidor,
      fechaExp: format(parseISO(usuario.fechaexp), "yyyy-MM-dd'T'HH:mm"),
      maxDispositivos: usuario.maxdispositivos,
      isAdmin: usuario.isadmin,
      telefono: usuario.telefono || '',
      telegram: usuario.telegram || ''
    });
    setFormVisible(true);
  };

  const eliminarUsuario = async (id) => {
    if (window.confirm('¿Eliminar este usuario?')) {
      try {
        await api.delete(`/usuarios/${id}`);
        setSuccess('Usuario eliminado');
        cargarUsuarios();
      } catch {
        setError('Error al eliminar usuario');
      }
    }
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
  
    try {
      const userData = {
        ...form,
        maxDispositivos: parseInt(form.maxDispositivos),
      };
  
      const response = editando 
        ? await api.put(`/usuarios/${editando.id}`, userData)
        : await api.post('/usuarios', userData);
  
      setSuccess(`Usuario ${editando ? 'actualizado' : 'creado'} correctamente. Estado: ${response.data?.estado || 'activo'}`);
      
      setFormVisible(false);
      setEditando(null);
      cargarUsuarios();
      
      // Resetear formulario
      setForm({
        username: '',
        password: '',
        servidor: 'Mundiemby1',
        fechaExp: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        maxDispositivos: 1,
        isAdmin: false,
        telefono: '',
        telegram: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const getEstado = (fecha) => {
    const hoy = new Date();
    const exp = parseISO(fecha);
    const dias = differenceInDays(exp, hoy);
    if (isBefore(exp, hoy)) return <span style={{ color: 'red' }}>Expirado</span>;
    if (dias <= 10) return <span style={{ color: 'yellow' }}>Por expirar ({dias}d)</span>;
    return <span style={{ color: 'green' }}>Active ({dias}d)</span>;
  };

  const getRowStyle = (fecha) => {
    const hoy = new Date();
    const exp = parseISO(fecha);
    if (isBefore(exp, hoy)) return { backgroundColor: '#661111', color: 'white' };
    if (differenceInDays(exp, hoy) <= 10) return { backgroundColor: '#665d00', color: 'white' };
    return { backgroundColor: '#2b2b2b', color: 'white' };
  };

  const cambiarOrden = (campo) => {
    if (orden.campo === campo) {
      setOrden({
        campo,
        direccion: orden.direccion === 'asc' ? 'desc' : 'asc'
      });
    } else {
      setOrden({
        campo,
        direccion: 'asc'
      });
    }
    setPaginaActual(1);
  };

  const usuariosFiltrados = usuarios.filter(u => {
    const hoy = new Date();
    const fecha = parseISO(u.fechaexp);
    
    // Filtro por estado
    if (filtro === 'caducados' && !isBefore(fecha, hoy)) return false;
    if (filtro === 'proximos' && !(isBefore(fecha, addDays(hoy, 10)) && !isBefore(fecha, hoy))) return false;
    if (filtroServidor && u.servidor !== filtroServidor) return false;

    // Filtro por búsqueda
    if (busqueda) {
      if (busquedaExacta) {
        if (u.username !== busqueda) return false;
      } else {
        if (!u.username.toLowerCase().includes(busqueda.toLowerCase())) return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    let comparacion = 0;
    
    if (orden.campo === 'username') {
      comparacion = a.username.localeCompare(b.username);
    } else if (orden.campo === 'fechaexp') {
      comparacion = new Date(a.fechaexp) - new Date(b.fechaexp);
    }
    
    return orden.direccion === 'asc' ? comparacion : -comparacion;
  });

  // Paginación
  const totalPaginas = Math.ceil(usuariosFiltrados.length / resultadosPorPagina);
  const indiceInicial = (paginaActual - 1) * resultadosPorPagina;
  const usuariosPagina = usuariosFiltrados.slice(indiceInicial, indiceInicial + resultadosPorPagina);

  const cambiarPagina = (pagina) => {
    setPaginaActual(pagina);
  };

  return (
    <div style={{ 
      backgroundColor: '#1e1e1e', 
      color: '#fff',
      minHeight: '100vh',
      padding: '20px',
      paddingTop: '90px'
    }}>
    
      {/* Cabecera fija con botón y título */}
      <div style={{
        position: 'fixed',
        top: '0',
        left: '0',
        zIndex: 1001,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        backgroundColor: '#1e1e1e',
        padding: '10px 20px',
        height: '60px',
        width: '100%'
      }}>
        <button 
          style={{
            backgroundColor: '#2b2b2b',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '6px 12px',
            borderRadius: '4px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setMenuAbierto(!menuAbierto)}
        >
          ☰
        </button>
        <h1 style={{
          fontSize: '24px',
          color: 'limegreen',
          margin: 0
        }}>
          MundiEmby
        </h1>
      </div>

      {/* Menú lateral usando el componente AdminMenu */}
      <AdminMenu 
        abierto={menuAbierto} 
        onCerrar={() => setMenuAbierto(false)}
        onNavegar={(ruta) => navigate(ruta)}
      />

      {/* Contenido principal */}
      <div style={{ 
        marginLeft: menuAbierto ? '270px' : '0',
        transition: 'margin-left 0.3s ease'
      }}>
        <h2 style={{ marginBottom: '20px' }}>Gestión de Usuarios</h2>

        {/* Controles superiores */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          gap: '15px'
        }}>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button 
              style={{
                padding: '8px 12px',
                backgroundColor: '#0078D7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={() => setFiltrosVisibles(!filtrosVisibles)}
            >
              {filtrosVisibles ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </button>
            
            <button 
              style={{
                padding: '8px 12px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }} 
              onClick={() => {
                setEditando(null);
                setForm({
                  username: '',
                  password: '',
                  servidor: 'Mundiemby1',
                  fechaExp: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
                  maxDispositivos: 1,
                  isAdmin: false,
                  telefono: '',
                  telegram: ''
                });
                setFormVisible(true);
              }}
            >
              <span role="img" aria-label="user">👤</span> Nuevo Usuario
            </button>
          </div>
        </div>

        {/* Filtros */}
        {filtrosVisibles && (
          <div style={{
            backgroundColor: '#2b2b2b',
            padding: '20px',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0 }}>Filtrar usuarios</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '15px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ display: 'block' }}>Nombre:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input 
                  type="text" 
                  style={{
                    flex: 1,
                    backgroundColor: '#444',
                    color: 'white',
                    border: '1px solid #666',
                    padding: '6px',
                    borderRadius: '4px'
                  }}
                  value={busqueda} 
                  onChange={e => {
                    setBusqueda(e.target.value);
                    setPaginaActual(1);
                  }} 
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', margin: 0, color: 'white' }}>
                  <input 
                    type="checkbox" 
                    checked={busquedaExacta}
                    onChange={e => setBusquedaExacta(e.target.checked)}
                  />
                  Exacto
                </label>
              </div>
            </div>
            

              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Servidor:</label>
                <select 
                  style={{
                    width: '100%',
                    backgroundColor: '#333',
                    color: 'white',
                    border: '1px solid #555',
                    padding: '6px',
                    borderRadius: '4px'
                  }}
                  value={filtroServidor}
                  onChange={e => {
                    setFiltroServidor(e.target.value);
                    setPaginaActual(1);
                  }}
                >
                  <option value="">Todos</option>
                  {servidores.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input 
                    type="checkbox" 
                    checked={filtro === 'caducados'}
                    onChange={e => setFiltro(e.target.checked ? 'caducados' : 'todos')}
                  />
                  Solo cuentas caducadas
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input 
                    type="checkbox" 
                    checked={filtro === 'proximos'}
                    onChange={e => setFiltro(e.target.checked ? 'proximos' : 'todos')}
                  />
                  Próximos a caducar (≤10 días)
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Tabla de usuarios */}
        <div style={{ 
          backgroundColor: '#2b2b2b',
          padding: '20px',
          borderRadius: '6px',
          overflowX: 'auto'
        }}>
          <table style={{ 
            width: '100%',
            borderCollapse: 'collapse',
            color: 'white'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#333' }}>
                <th style={{ padding: '10px', border: '1px solid #444' }}>#</th>
                <th 
                  style={{ 
                    padding: '10px', 
                    border: '1px solid #444',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => cambiarOrden('username')}
                >
                  Usuario
                  {orden.campo === 'username' && (
                    <span style={{ marginLeft: '5px' }}>
                      {orden.direccion === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th style={{ padding: '10px', border: '1px solid #444' }}>Servidor</th>
                <th style={{ padding: '10px', border: '1px solid #444' }}>Contraseña</th>
                <th 
                 style={{ 
                   padding: '10px', 
                   border: '1px solid #444', 
                   width: '200px', 
                   cursor: 'pointer',
                   position: 'relative'
                 }}
                 onClick={() => cambiarOrden('fechaexp')}
               >
                 Fecha de expiración
                 {orden.campo === 'fechaexp' && (
                   <span style={{ marginLeft: '5px' }}>
                     {orden.direccion === 'asc' ? '↑' : '↓'}
                   </span>
                 )}
               </th>
                <th style={{ padding: '10px', border: '1px solid #444' }}>Estado</th>
                <th style={{ padding: '10px', border: '1px solid #444', width: '80px' }}>Dispositivos</th>
                <th style={{ padding: '10px', border: '1px solid #444' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuariosPagina.map((u, i) => (
                <tr key={u.id} style={getRowStyle(u.fechaexp)}>
                  <td style={{ padding: '10px', border: '1px solid #444' }}>{indiceInicial + i + 1}</td>
                  <td style={{ padding: '10px', border: '1px solid #444' }}>
                    {u.username } {u.isadmin && <span style={{ color: '#0078D7' }}>(admin)</span>}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #444' }}>{u.servidor}</td>
                  <td style={{ padding: '10px', border: '1px solid #444' }}>{u.password}</td>
                  <td style={{ padding: '10px', border: '1px solid #444' }}>
                    {format(parseISO(u.fechaexp), 'yyyy-MM-dd HH:mm:ss')}
                  </td>
                  <td style={{ padding: '10px', border: '1px solid #444' }}>{getEstado(u.fechaexp)}</td>
                  <td style={{ padding: '10px', border: '1px solid #444' }}>{u.maxdispositivos}</td>
                  <td style={{ padding: '10px', border: '1px solid #444', display: 'flex', gap: '5px' }}>
                    <button 
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        color: '#0078D7',
                        border: '1px solid #0078D7',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={() => iniciarEdicion(u)}
                    >
                      Editar
                    </button>
                    <button 
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        color: 'crimson',
                        border: '1px solid crimson',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onClick={() => eliminarUsuario(u.id)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Paginación */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            color: 'white'
          }}>
            <div>
              Mostrando {indiceInicial + 1} a {Math.min(indiceInicial + resultadosPorPagina, usuariosFiltrados.length)} de {usuariosFiltrados.length} usuarios
            </div>
            
            <div style={{ display: 'flex', gap: '5px' }}>
              <select 
                style={{
                  backgroundColor: '#000',
                  color: 'white',
                  border: '1px solid #666',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease'
                }}
                value={resultadosPorPagina}
                onChange={e => {
                  setResultadosPorPagina(Number(e.target.value));
                  setPaginaActual(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '5px' }}>
              <button 
                style={{
                  padding: '8px 12px',
                  backgroundColor: paginaActual === 1 ? '#555' : '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: paginaActual === 1 ? 'default' : 'pointer'
                }}
                onClick={() => cambiarPagina(1)}
                disabled={paginaActual === 1}
              >
                «
              </button>
              <button 
                style={{
                  padding: '8px 12px',
                  backgroundColor: paginaActual === 1 ? '#555' : '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: paginaActual === 1 ? 'default' : 'pointer'
                }}
                onClick={() => cambiarPagina(paginaActual - 1)}
                disabled={paginaActual === 1}
              >
                ‹
              </button>
              
              {[...Array(totalPaginas).keys()]
                .slice(Math.max(0, paginaActual - 3), Math.min(totalPaginas, paginaActual + 2))
                .map(i => (
                  <button 
                    key={i + 1}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: paginaActual === i + 1 ? '#0078D7' : '#333',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: paginaActual === i + 1 ? 'bold' : 'normal'
                    }}
                    onClick={() => cambiarPagina(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              
              <button 
                style={{
                  padding: '8px 12px',
                  backgroundColor: paginaActual === totalPaginas ? '#555' : '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: paginaActual === totalPaginas ? 'default' : 'pointer'
                }}
                onClick={() => cambiarPagina(paginaActual + 1)}
                disabled={paginaActual === totalPaginas}
              >
                ›
              </button>
              <button 
                style={{
                  padding: '8px 12px',
                  backgroundColor: paginaActual === totalPaginas ? '#555' : '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: paginaActual === totalPaginas ? 'default' : 'pointer'
                }}
                onClick={() => cambiarPagina(totalPaginas)}
                disabled={paginaActual === totalPaginas}
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para nuevo/editar usuario */}
      {formVisible && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setFormVisible(false)}
        >
          <div 
            style={{ 
              backgroundColor: '#2b2b2b',
              padding: '20px',
              borderRadius: '6px',
              width: '500px',
              maxWidth: '90%'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '1px solid #444'
            }}>
              <h3 style={{ margin: 0, color: 'white' }}>
                {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button 
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
                onClick={() => setFormVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              {/* Fila 1: Usuario y Contraseña */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: 'white' }}>Usuario</label>
                  <input 
                    type="text" 
                    name="username" 
                    style={{
                      width: '93%',
                      backgroundColor: '#444',
                      color: 'white',
                      border: '1px solid #666',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                    value={form.username} 
                    onChange={handleChange} 
                    required 
                    disabled={!!editando} 
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: 'white' }}>Contraseña</label>
                  <input 
                    type="text"
                    name="password"
                    style={{
                      width: '100%',
                      backgroundColor: '#444',
                      color: 'white',
                      border: '1px solid #666',
                      padding: '5px 8px',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                    value={form.password}
                    onChange={handleChange}
                    required={!editando}
                  />
                </div>
              </div>
            
              {/* Fila 2: Servidor y Fecha */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: 'white' }}>Servidor</label>
                  <select 
                    name="servidor" 
                    style={{
                      width: '100%',
                      backgroundColor: '#333',
                      color: 'white',
                      border: '1px solid #555',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                    value={form.servidor} 
                    onChange={handleChange}
                  >
                    {servidores.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: 'white' }}>Fecha Expiración</label>
                  <input 
                    type="datetime-local" 
                    name="fechaExp" 
                    style={{
                      width: '100%',
                      backgroundColor: '#444',
                      color: 'white',
                      border: '1px solid #666',
                      padding: '5px 8px',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                    value={form.fechaExp}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            
              {/* Fila 3: Dispositivos y Teléfono */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: 'white' }}>Dispositivos</label>
                  <input 
                    type="number" 
                    name="maxDispositivos" 
                    style={{
                      width: '93%',
                      backgroundColor: '#444',
                      color: 'white',
                      border: '1px solid #666',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                    value={form.maxDispositivos} 
                    onChange={handleChange} 
                    min="1" 
                    required 
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: 'white' }}>Teléfono</label>
                  <input 
                    type="text" 
                    name="telefono" 
                    style={{
                      width: '95%',
                      backgroundColor: '#444',
                      color: 'white',
                      border: '1px solid #666',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                    value={form.telefono} 
                    onChange={handleChange} 
                  />
                </div>
              </div>
            
              {/* Fila 4: Telegram y Administrador */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', color: 'white' }}>Telegram</label>
                  <input 
                    type="text" 
                    name="telegram" 
                    style={{
                      width: '93%',
                      backgroundColor: '#444',
                      color: 'white',
                      border: '1px solid #666',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                    value={form.telegram} 
                    onChange={handleChange} 
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      name="isAdmin" 
                      checked={form.isAdmin} 
                      onChange={handleChange} 
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ color: 'white' }}>¿Administrador?</span>
                  </label>
                </div>
              </div>
            
              {/* Botones */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button 
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }} 
                  type="button" 
                  onClick={() => setFormVisible(false)}
                >
                  Cancelar
                </button>
                <button 
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#0078D7',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }} 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : editando ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsuarios;